import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Plus, Wallet, Trash2, Receipt, ShoppingBag, 
    Car, Wrench, Gift, Coffee, AlertCircle, Calendar
} from 'lucide-react';

// Catégories de décaissement
const DISBURSEMENT_CATEGORIES = [
    { value: 'achat_spontane', label: 'Achat spontané', icon: ShoppingBag, color: 'bg-blue-100 text-blue-700' },
    { value: 'transport', label: 'Transport', icon: Car, color: 'bg-green-100 text-green-700' },
    { value: 'reparation', label: 'Réparation / Entretien', icon: Wrench, color: 'bg-orange-100 text-orange-700' },
    { value: 'pourboire', label: 'Pourboire', icon: Gift, color: 'bg-purple-100 text-purple-700' },
    { value: 'consommation', label: 'Consommation équipe', icon: Coffee, color: 'bg-amber-100 text-amber-700' },
    { value: 'urgence', label: 'Urgence', icon: AlertCircle, color: 'bg-red-100 text-red-700' },
    { value: 'autre', label: 'Autre', icon: Receipt, color: 'bg-gray-100 text-gray-700' },
];

const DisbursementsPage = () => {
    const { api, hasPermission, user } = useAuth();
    const [disbursements, setDisbursements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const canCreate = hasPermission('create_daily_output') || 
                      ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);

    const [formData, setFormData] = useState({
        category: '',
        amount: '',
        description: '',
        beneficiary: '',
        notes: ''
    });

    useEffect(() => {
        fetchDisbursements();
    }, [selectedDate]);

    const fetchDisbursements = async () => {
        try {
            const response = await api().get(`/disbursements?date=${selectedDate}`);
            setDisbursements(response.data);
        } catch (error) {
            // If endpoint doesn't exist yet, show empty list
            setDisbursements([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        
        if (!formData.category || !formData.amount || !formData.description) {
            toast.error('Remplissez tous les champs obligatoires');
            return;
        }

        try {
            const response = await api().post('/disbursements', {
                ...formData,
                amount: parseFloat(formData.amount),
                date: selectedDate
            });
            setDisbursements([response.data, ...disbursements]);
            toast.success('Décaissement enregistré');
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce décaissement ?')) return;
        
        try {
            await api().delete(`/disbursements/${id}`);
            setDisbursements(disbursements.filter(d => d.id !== id));
            toast.success('Décaissement supprimé');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const resetForm = () => {
        setFormData({
            category: '',
            amount: '',
            description: '',
            beneficiary: '',
            notes: ''
        });
    };

    const getCategoryInfo = (categoryValue) => {
        return DISBURSEMENT_CATEGORIES.find(c => c.value === categoryValue) || DISBURSEMENT_CATEGORIES[6];
    };

    const totalToday = disbursements.reduce((sum, d) => sum + (d.amount || 0), 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="disbursements-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Décaissements</h1>
                    <p className="text-muted-foreground">Sorties d'argent du jour (achats spontanés, transport, etc.)</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="w-40"
                        />
                    </div>
                    {canCreate && (
                        <Button 
                            onClick={() => { resetForm(); setDialogOpen(true); }}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="add-disbursement-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau Décaissement
                        </Button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-red-100 dark:bg-red-800 rounded-lg">
                                <Wallet className="w-6 h-6 text-red-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total du jour</p>
                                <p className="text-2xl font-bold text-red-600">{totalToday.toLocaleString()} FCFA</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                <Receipt className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Nb Décaissements</p>
                                <p className="text-2xl font-bold">{disbursements.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-amber-100 dark:bg-amber-900 rounded-lg">
                                <ShoppingBag className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Moyenne</p>
                                <p className="text-2xl font-bold">
                                    {disbursements.length > 0 ? Math.round(totalToday / disbursements.length).toLocaleString() : 0} FCFA
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* List */}
            <Card>
                <CardHeader>
                    <CardTitle>Décaissements du {new Date(selectedDate).toLocaleDateString('fr-FR')}</CardTitle>
                </CardHeader>
                <CardContent>
                    {disbursements.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Aucun décaissement pour cette date</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Catégorie</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Bénéficiaire</TableHead>
                                    <TableHead className="text-right">Montant</TableHead>
                                    <TableHead>Par</TableHead>
                                    <TableHead>Heure</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {disbursements.map(d => {
                                    const cat = getCategoryInfo(d.category);
                                    const Icon = cat.icon;
                                    return (
                                        <TableRow key={d.id}>
                                            <TableCell>
                                                <Badge className={cat.color}>
                                                    <Icon className="w-3 h-3 mr-1" />
                                                    {cat.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-medium">{d.description}</TableCell>
                                            <TableCell>{d.beneficiary || '-'}</TableCell>
                                            <TableCell className="text-right font-bold text-red-600">
                                                -{d.amount?.toLocaleString()} FCFA
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {d.created_by_name || '-'}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {d.created_at ? new Date(d.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'}) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {canCreate && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(d.id)}
                                                        className="text-red-600 hover:text-red-700"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Wallet className="w-5 h-5 text-red-600" />
                            Nouveau Décaissement
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Catégorie *</Label>
                            <Select
                                value={formData.category}
                                onValueChange={(val) => setFormData({ ...formData, category: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Sélectionner une catégorie" />
                                </SelectTrigger>
                                <SelectContent>
                                    {DISBURSEMENT_CATEGORIES.map(cat => {
                                        const Icon = cat.icon;
                                        return (
                                            <SelectItem key={cat.value} value={cat.value}>
                                                <span className="flex items-center gap-2">
                                                    <Icon className="w-4 h-4" />
                                                    {cat.label}
                                                </span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="amount">Montant (FCFA) *</Label>
                            <Input
                                id="amount"
                                type="number"
                                min="0"
                                value={formData.amount}
                                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                placeholder="0"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">Description *</Label>
                            <Input
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Ex: Achat d'épices au marché"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="beneficiary">Bénéficiaire</Label>
                            <Input
                                id="beneficiary"
                                value={formData.beneficiary}
                                onChange={(e) => setFormData({ ...formData, beneficiary: e.target.value })}
                                placeholder="Ex: Fournisseur, Taxi, etc."
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="notes">Notes</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Remarques additionnelles..."
                                rows={2}
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-red-600 hover:bg-red-700">
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DisbursementsPage;
